<template>
  <div class="row referral-ambassador-requests">
    <div class="col-md-12">
      <div class="search-bar referral-ambassador-requests__filters">
        <div class="col-sm-4">
          <label>{{ $t('referralProgram.requestStatus') }}</label>
          <select v-model="statusFilter" class="form-control" @change="reload">
            <option value="">{{ $t('general.all') }}</option>
            <option value="PENDING">{{ $t('general.pending') }}</option>
            <option value="APPROVED">{{ $t('general.accepted') }}</option>
            <option value="DENIED">{{ $t('general.denied') }}</option>
          </select>
        </div>
      </div>

      <div class="card">
        <div class="card-content row">
          <data-tables-server
            @query-change="paginationChanged"
            v-loading="loading"
            :data="tableData"
            :total="total"
            :pagination-props="paginationProps"
            style="width: 100%"
          >
            <el-table-column prop="id" :label="$t('customers.id')" min-width="70" />
            <el-table-column min-width="160" :label="$t('reports.email')">
              <template slot-scope="props">
                {{ props.row.email || props.row.user_email || (props.row.user && props.row.user.email) || $t('general.emDash') }}
              </template>
            </el-table-column>
            <el-table-column min-width="120" :label="$t('referralProgram.desiredCode')">
              <template slot-scope="props">
                {{ props.row.desired_code || $t('general.emDash') }}
              </template>
            </el-table-column>
            <el-table-column min-width="100" :label="$t('referralProgram.requestStatus')">
              <template slot-scope="props">
                {{ formatRequestStatus(props.row) }}
              </template>
            </el-table-column>
            <el-table-column min-width="160" :label="$t('referralProgram.createdAt')">
              <template slot-scope="props">
                {{ formatRequestCreatedAt(props.row) }}
              </template>
            </el-table-column>
            <el-table-column min-width="220" :label="$t('marketingCampaigns.actions')">
              <template slot-scope="props">
                <div v-if="isPending(props.row)" class="referral-request-actions">
                  <a
                    class="btn add"
                    href="#"
                    :title="$t('general.accept')"
                    @click.prevent="openApprove(props.row)"
                  >
                    {{ $t('general.accept') }}
                  </a>
                  <a
                    class="btn delete"
                    href="#"
                    :title="$t('general.deny')"
                    @click.prevent="openDeny(props.row)"
                  >
                    {{ $t('general.deny') }}
                  </a>
                </div>
                <span v-else class="text-muted">{{ $t('general.emDash') }}</span>
              </template>
            </el-table-column>
          </data-tables-server>
        </div>
      </div>

      <vmodal name="referralApproveRequestModal" height="auto" width="500" :adaptive="true" @closed="resetApprove">
        <div class="card referral-approve-request-modal" style="margin: 0;">
          <div class="card-content">
            <div class="modal-header">
              <p>{{ $t('referralProgram.approveRequest') }}</p>
            </div>
            <div class="modal-body">
              <div class="form-group" :class="{ 'has-error': campaignSelectInvalid }">
                <label :class="{ 'text-danger': campaignSelectInvalid }">{{ $t('referralProgram.selectCampaign') }}</label>
                <select
                  v-model="approveForm.campaignId"
                  class="form-control"
                  :class="{ error: campaignSelectInvalid }"
                  @change="clearApproveCampaignError"
                >
                  <option disabled value="">{{ $t('general.select') }}</option>
                  <option v-for="c in campaigns" :key="c.id" :value="c.id">{{ c.name || $t('general.idHash', { id: c.id }) }}</option>
                </select>
                <small class="text-danger" v-if="campaignSelectInvalid">{{ $t('referralProgram.selectCampaignRequired') }}</small>
              </div>
              <div class="form-group">
                <label>{{ $t('referralProgram.approvedCodeOptional') }}</label>
                <input v-model="approveForm.code" type="text" class="form-control" />
              </div>
            </div>
            <div class="modal-footer referral-approve-request-modal__footer referral-request-actions">
              <a
                href="#"
                class="btn add"
                :class="{ disabled: !canSubmitApprove }"
                @click.prevent="submitApprove"
              >{{ $t('general.accept') }}</a>
              <a href="#" class="btn delete" @click.prevent="closeApproveModal">{{ $t('general.cancel2') }}</a>
            </div>
          </div>
        </div>
      </vmodal>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import DataTables from 'vue-data-tables'
import vmodal from 'vue-js-modal'
import swal from 'sweetalert2'

Vue.use(DataTables)
Vue.use(vmodal, { componentName: 'vmodal' })

export default {
  props: {
    isAmbassadorRequests: { type: Boolean, default: false }
  },
  watch: {
    isAmbassadorRequests (v) {
      if (v) this.reload()
    },
    campaigns (list) {
      if (list.length && this.selectedRequest && !this.approveForm.campaignId) {
        this.approveForm.campaignId = list[0].id
      }
    }
  },
  data () {
    return {
      loading: false,
      statusFilter: '',
      tableData: [],
      total: 0,
      paginationProps: { layout: 'prev, pager, next, sizes, total' },
      campaigns: [],
      approveForm: { campaignId: '', code: '' },
      approveCampaignError: false,
      selectedRequest: null,
      timezone: ((this.$session.get('selectedCompanyData') || {}).timezone) || 'Europe/Bucharest'
    }
  },
  computed: {
    canSubmitApprove () {
      return !!this.approveForm.campaignId
    },
    campaignSelectInvalid () {
      return this.approveCampaignError && !this.approveForm.campaignId
    }
  },
  mounted () {
    this.loadCampaigns()
    this.reload()
  },
  methods: {
    formatRequestCreatedAt (row) {
      const raw = row && (row.created_at || row.createdAt)
      if (!raw) return this.$t('general.emDash')
      const value = typeof raw === 'object' && raw.date ? raw.date : raw
      if (!value) return this.$t('general.emDash')
      return this.formatToCompanyTimezone(value, this.timezone)
    },
    formatRequestStatus (row) {
      const s = (row && row.status ? String(row.status) : '').trim().toUpperCase()
      const keys = {
        PENDING: 'general.pending',
        APPROVED: 'general.accepted',
        DENIED: 'general.denied',
        REJECTED: 'general.denied',
        CANCELLED: 'reports.canceled'
      }
      const path = keys[s]
      if (path) return this.$t(path)
      return s ? this.$t('general.statusUnknown') : this.$t('general.emDash')
    },
    isPending (row) {
      const s = (row.status || '').toUpperCase()
      return s === 'PENDING'
    },
    normalizeRows (response) {
      if (!response || !response.data) return []
      const d = response.data
      return Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : [])
    },
    normalizeTotal (response) {
      if (response.pagination && response.pagination.total != null) return response.pagination.total
      if (response.meta && response.meta.total != null) return response.meta.total
      const d = response.data
      if (d && d.total != null) return d.total
      return this.normalizeRows(response).length
    },
    loadCampaigns () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/referral-campaign', 'GET', data, (response) => {
        if (response.status) {
          const rows = this.normalizeRows(response)
          this.campaigns = rows
        }
      })
    },
    paginationChanged (query) {
      this.fetchPage(query.page, query.pageSize)
    },
    reload () {
      this.fetchPage(1, 10)
    },
    fetchPage (page, perPage) {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        page,
        perPage
      }
      if (this.statusFilter) {
        data.status = this.statusFilter
      }
      this.apiRequest('/referral-ambassador-request', 'GET', data, (response) => {
        if (response.status) {
          this.tableData = this.normalizeRows(response)
          this.total = this.normalizeTotal(response)
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
        this.loading = false
      })
    },
    defaultCampaignId () {
      const first = this.campaigns[0]
      return first && first.id != null ? first.id : ''
    },
    openApprove (row) {
      this.selectedRequest = row
      this.approveForm = {
        campaignId: this.defaultCampaignId(),
        code: row.desired_code || ''
      }
      this.$modal.show('referralApproveRequestModal')
    },
    closeApproveModal () {
      this.$modal.hide('referralApproveRequestModal')
      this.resetApprove()
    },
    resetApprove () {
      this.selectedRequest = null
      this.approveForm = { campaignId: '', code: '' }
      this.approveCampaignError = false
    },
    clearApproveCampaignError () {
      this.approveCampaignError = false
    },
    submitApprove () {
      if (!this.selectedRequest) return
      if (!this.approveForm.campaignId) {
        this.approveCampaignError = true
        return
      }
      const body = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        decision: 'APPROVE',
        referral_campaign_id: Number(this.approveForm.campaignId),
        approved_referral_code: this.approveForm.code || undefined
      }
      this.loading = true
      this.apiRequest('/referral-ambassador-request/' + this.selectedRequest.id, 'PUT', body, (response) => {
        this.loading = false
        if (response.status) {
          this.closeApproveModal()
          swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          this.reload()
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
      })
    },
    openDeny (row) {
      swal({
        title: this.$t('referralProgram.denyReason'),
        input: 'text',
        showCancelButton: true,
        confirmButtonText: this.$t('general.confirm'),
        cancelButtonText: this.$t('general.cancel')
      }).then((result) => {
        if (!result) return
        const reason = typeof result === 'string' ? result.trim() : ''
        const body = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany'),
          decision: 'DENY',
          denied_reason: reason || undefined
        }
        this.loading = true
        this.apiRequest('/referral-ambassador-request/' + row.id, 'PUT', body, (response) => {
          this.loading = false
          if (response.status) {
            swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
            this.reload()
          } else {
            swal('', this.$t('validations.wentWrong'), 'error')
          }
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.referral-ambassador-requests__filters {
  margin-bottom: 1.5rem;
}
</style>

<style lang="scss">
/* Shared button row: Accept (add) then Deny/Cancel (delete) */
.referral-request-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;

  a.add,
  a.delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    float: none;
    margin: 0;
    flex: 0 0 auto;
    box-sizing: border-box;
  }
}

.referral-ambassador-requests .referral-request-actions {
  justify-content: flex-start;
}

/* Modal footer: same order, aligned right */
.referral-approve-request-modal__footer.referral-request-actions a.add.disabled {
  opacity: 0.55;
  pointer-events: none;
  cursor: not-allowed;
}

.referral-approve-request-modal__footer.referral-request-actions {
  width: 100%;
  margin: 0 !important;
  padding: 10px 16px 12px !important;
  border-top: 1px solid #e9e9e9 !important;
  justify-content: flex-end !important;

  &::before,
  &::after {
    display: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/Referral/ReferralAmbassadorRequests.vue